/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useCallback, useMemo, useState } from "react";

import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button } from "@forge/common";

import DeviceList from "../devicelist";
import CreateDevice from "../createdevice";

import { DeviceListProps } from "../../models/devicelist";
import { setDeviceListUpdated } from "../../actions";
import { LOCALE_CONST } from "../../utils/constants";
import { useLinkGatewayToSite } from "../../hooks/useLinkGatewayToSite";

import "./index.scss";

const EXISTING_TAB = "existingGateway";
const CREATE_TAB = "createNew";

// Content of the Assign gateway sidebar; the header comes from the parent Sidebar and there is no footer.
const AssignGatewayPanel = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { linkGateway } = useLinkGatewayToSite();

  const [tabValue, setTabValue] = useState(EXISTING_TAB);
  const [linkingDeviceId, setLinkingDeviceId] = useState("");

  const handleLink = useCallback(
    async (item: DeviceListProps) => {
      setLinkingDeviceId(item.deviceId);
      const result = await linkGateway(item.deviceId);
      setLinkingDeviceId("");

      if (result === "linked" || result === "skipped") {
        dispatch(setDeviceListUpdated({ deviceListUpdated: true }));
      }
    },
    [linkGateway, dispatch],
  );

  const siteLink = useMemo(
    () => ({
      link: (deviceId: string) => linkGateway(deviceId, { notify: false }),
      onDone: () => setTabValue(EXISTING_TAB),
      onCancel: () => setTabValue(EXISTING_TAB),
    }),
    [linkGateway],
  );

  return (
    <div className="assign-gateway-panel">
      <div className="assign-gateway-tabs inline-flex gap-1 p-1 rounded-lg bg-gray-100 dark:bg-gray-800" role="tablist">
        {[
          { id: EXISTING_TAB, label: t(LOCALE_CONST.existingGatewayTab) },
          { id: CREATE_TAB, label: t(LOCALE_CONST.createNewTab) },
        ].map((tab) => (
          <Button
            key={tab.id}
            role="tab"
            aria-selected={tabValue === tab.id}
            data-testid={`tab-${tab.id}`}
            variant={tabValue === tab.id ? "primary" : "quiet-secondary"}
            onClick={() => setTabValue(tab.id)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {tabValue === EXISTING_TAB && (
        <DeviceList onLinkGateway={handleLink} linkingDeviceId={linkingDeviceId} />
      )}
      {tabValue === CREATE_TAB && <CreateDevice siteLink={siteLink} />}
    </div>
  );
};

export default AssignGatewayPanel;
